import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Breadcrumbs,
  Link,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { Home as HomeIcon, Assessment as AssessmentIcon } from '@mui/icons-material';
import TuneIcon from '@mui/icons-material/Tune';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import {
  DataGridPro,
  GridToolbar,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-pro';
import { useToast } from '../../hooks/useToast';
import useFetchApi from '../../hooks/useFetchApi';
import { useUserRights } from '../../../context/UserRightsContext';
import { FC } from '../../../constants/functionCodes';
import {
  matchesWorkflowProductScope,
  workflowProductFunctionCode,
  workflowProductTitle,
} from './workflowProductScope';
import { formatDate, formatAmount, formatPrice, formatQuantity, initializeFormats, getFormatConfig } from '../../../utils/global';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { AuditLoggers } from '../../../utils/auditLogger';
import ColumnCustomizationModal from './ColumnCustomizationModal';
import { useGridRootProps } from '@mui/x-data-grid-pro';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateFormFieldsSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
} from '../../../theme/harmonateStyles';

const harmonateBreadcrumbSx = {
  fontSize: '0.9rem',
  '& .MuiBreadcrumbs-li': { color: ht.white },
  '& a': { color: ht.white },
  '& .MuiBreadcrumbs-separator': { color: ht.white },
};

// Parse date string using app format (e.g. DD/MM/YYYY, MM/DD/YYYY).
function parseDateWithFormat(valueStr, formatStr) {
  if (!valueStr || typeof valueStr !== 'string') return null;
  const s = valueStr.trim();
  const sep = (formatStr && formatStr.match(/[^a-zA-Z]/))?.[0] || '/';
  const fmtParts = formatStr ? formatStr.split(sep) : [];
  const valParts = s.split(sep);
  if (fmtParts.length !== 3 || valParts.length !== 3) return null;
  let y, m, d;
  for (let i = 0; i < 3; i++) {
    const p = (fmtParts[i] || '').toUpperCase();
    const v = parseInt(valParts[i], 10);
    if (Number.isNaN(v)) return null;
    if (p.includes('Y')) y = v;
    else if (p.includes('M')) m = v;
    else if (p.includes('D')) d = v;
  }
  if (y == null || m == null || d == null) return null;
  const date = new Date(y, m - 1, d);
  return isNaN(date.getTime()) ? null : date;
}

function dateToISOString(date) {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const dateRegex = /(\d+)-(\d+)-(\d+)/;
function buildDateApplyFilterFn(filterItem, compareFn) {
  if (!filterItem.value) return null;
  const m = String(filterItem.value).trim().match(dateRegex);
  if (!m) return null;
  const [year, month, day] = m.slice(1).map(Number);
  const time = new Date(year, month - 1, day).getTime();
  return (value) => {
    if (!value) return false;
    const dateCopy = new Date(value);
    const timeToCompare = dateCopy.setHours(0, 0, 0, 0);
    return compareFn(timeToCompare, time);
  };
}

// Filter value input that displays and accepts dates in app-config format.
function DateFilterInput({ item, applyValue, apiRef, focusElementRef, tabIndex, disabled, clearButton }) {
  const dateFormat = getFormatConfig()?.DateFormat || 'DD/MM/YYYY';
  const [filterValueState, setFilterValueState] = useState('');
  const rootProps = useGridRootProps();
  const timeoutRef = useRef(null);
  const isFocusedRef = useRef(false);

  const rawToDisplay = (raw) => {
    if (raw == null || raw === '') return '';
    try {
      return formatDate(raw, dateFormat);
    } catch {
      return String(raw);
    }
  };

  useEffect(() => {
    if (isFocusedRef.current) return;
    setFilterValueState(rawToDisplay(item.value ?? ''));
  }, [item.value, dateFormat]);

  const onFilterChange = useCallback((e) => {
    const input = e.target.value;
    setFilterValueState(input);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      let valueToApply = '';
      if (input.trim()) {
        let parsed = parseDateWithFormat(input, dateFormat);
        if (!parsed && /^\d{4}-\d{2}-\d{2}$/.test(input.trim())) {
          const parts = input.trim().split('-').map(Number);
          if (parts.length === 3 && !parts.some(isNaN)) {
            parsed = new Date(parts[0], parts[1] - 1, parts[2]);
          }
        }
        if (parsed) valueToApply = dateToISOString(parsed);
        else valueToApply = input;
      }
      applyValue({ ...item, value: valueToApply });
    }, rootProps.filterDebounceMs ?? 150);
  }, [applyValue, item, dateFormat, rootProps.filterDebounceMs]);

  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);

  return (
    <TextField
      fullWidth
      label={apiRef.current.getLocaleText('filterPanelInputLabel')}
      placeholder={dateFormat}
      value={filterValueState}
      onChange={onFilterChange}
      onFocus={() => { isFocusedRef.current = true; }}
      onBlur={() => { isFocusedRef.current = false; }}
      variant="standard"
      type="text"
      inputRef={focusElementRef}
      tabIndex={tabIndex}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
      InputProps={clearButton ? { endAdornment: clearButton } : {}}
    />
  );
}

function getDateFilterOperators() {
  return [
    { value: 'is', getApplyFilterFnV7: (fi) => buildDateApplyFilterFn(fi, (a, b) => a === b), InputComponent: DateFilterInput },
    { value: 'not', getApplyFilterFnV7: (fi) => buildDateApplyFilterFn(fi, (a, b) => a !== b), InputComponent: DateFilterInput },
    { value: 'after', getApplyFilterFnV7: (fi) => buildDateApplyFilterFn(fi, (a, b) => a > b), InputComponent: DateFilterInput },
    { value: 'onOrAfter', getApplyFilterFnV7: (fi) => buildDateApplyFilterFn(fi, (a, b) => a >= b), InputComponent: DateFilterInput },
    { value: 'before', getApplyFilterFnV7: (fi) => buildDateApplyFilterFn(fi, (a, b) => a < b), InputComponent: DateFilterInput },
    { value: 'onOrBefore', getApplyFilterFnV7: (fi) => buildDateApplyFilterFn(fi, (a, b) => a <= b), InputComponent: DateFilterInput },
    { value: 'isEmpty', getApplyFilterFnV7: () => (v) => v == null, requiresFilterValue: false },
    { value: 'isNotEmpty', getApplyFilterFnV7: () => (v) => v != null, requiresFilterValue: false },
  ];
}

const OutputViewer = ({ onBackToWorkflows, productScope = null }) => {
  const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [selectedStep, setSelectedStep] = useState('');
  const [loading, setLoading] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [steps, setSteps] = useState([]);
  const [outputData, setOutputData] = useState([]);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [originalConfig, setOriginalConfig] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [customViewName, setCustomViewName] = useState('');
  const [savedViews, setSavedViews] = useState([]);
  const [saveModeDialogOpen, setSaveModeDialogOpen] = useState(false);
  const [saveModeLoading, setSaveModeLoading] = useState(false);
  const [appliedSavedViewName, setAppliedSavedViewName] = useState(null);
  const { showError, showSuccess } = useToast();
  
  // Get user rights for export permission
  const { hasPermission, isHarmonateUser } = useUserRights();
  const scopedFunctionCode = workflowProductFunctionCode(productScope);
  const canView = isHarmonateUser
    || (scopedFunctionCode
      ? hasPermission(scopedFunctionCode, 'view')
      : (hasPermission(FC.DOCUMENT_VALIDATION, 'view') || hasPermission(FC.ETVL_ENGINE, 'view')));
  const canExport = isHarmonateUser
    || (scopedFunctionCode
      ? hasPermission(scopedFunctionCode, 'export')
      : (hasPermission(FC.DOCUMENT_VALIDATION, 'export') || hasPermission(FC.ETVL_ENGINE, 'export')));
  const pageTitle = workflowProductTitle(productScope);

  // Get tenant ID from local storage
  const getTenantId = () => {
    return localStorage.getItem('tenant_id');
  };

  // Initialize formats when component mounts
  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      const tenantId = getTenantId();
      if (!tenantId) {
        console.error('No tenant ID found in local storage');
        setIsConfigLoaded(true);
        return;
      }

      try {
        await initializeFormats(tenantId);
        console.log('Format config initialized');
      } catch (error) {
        console.error('Failed to initialize formats:', error);
        showError('Using default number and date formats');
      } finally {
        if (isMounted) {
          setIsConfigLoaded(true);
        }
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch workflows using useFetchApi hook
  const tenantId = getTenantId();

  const { data: workflowsData, isLoading: isLoadingWorkflows } = useFetchApi({
    endpoint: `conductor/tenant-document-type-workflow-snowflake/tenant/${tenantId}?category=WF`,
    resGetter: (res) => res?.data || [],
    retrieveOnMount: true,
    Menu_id: 'WORKFLOWS',
  });

  // Update workflows state when data is loaded
  useEffect(() => {
    if (workflowsData) {
      const formattedWorkflows = workflowsData
        .filter(workflow => workflow.workflowDefinitionName !== 'Unidentified')
        .map((workflow, index) => {
          const id = workflow.workflowId || workflow.id || `workflow-${index}`;
          return {
            id,
            name: workflow.workflowDefinitionName || workflow.workflowName || workflow.name || `Workflow ${id}`,
            workflowDefinitionSnowflakeId: workflow.workflowDefinitionSnowflakeId || ''
          };
        })
        .filter((workflow) => matchesWorkflowProductScope(workflow.name, productScope));
      setWorkflows(formattedWorkflows);
    }
  }, [workflowsData, productScope]);

  // Handle workflow selection
  const handleWorkflowChange = (event) => {
    const workflowId = event.target.value;
    setSelectedWorkflowId(workflowId);

    const selectedWorkflowData = workflows.find(wf => wf.id === workflowId);
    setSelectedWorkflow(selectedWorkflowData || null);
    setSelectedStep('');
    setOutputData([]);
  };

  // Fetch steps for the selected workflow
  const { data: stepsData, isLoading: isLoadingSteps, refetch: fetchSteps } = useFetchApi({
    endpoint: `conductor/workflow-steps/tenant/${tenantId}?tenantWorkflowId=${selectedWorkflow?.workflowDefinitionSnowflakeId || ''}`,
    resGetter: (res) => res?.data || [],
    retrieveOnMount: false,
    enabled: false,
    Menu_id: 'WORKFLOWS',
  });

  // Fetch steps when selectedWorkflow changes
  useEffect(() => {
    if (selectedWorkflow?.workflowDefinitionSnowflakeId) {
      fetchSteps();
    } else {
      setSteps([]);
      setSelectedStep('');
    }
  }, [selectedWorkflow, fetchSteps]);

  // Update steps state when steps data is loaded
  useEffect(() => {
    if (!selectedWorkflow) {
      setSteps([]);
      setSelectedStep('');
      setOutputData([]);
      return;
    }

    if (stepsData && stepsData.length > 0) {
      const formattedSteps = stepsData.map((step, index) => {
        const stepId = step.stepId || step.id || `step-${index}`;
        const stepName = step.stepName || step.name || `Step ${stepId}`;
        return {
          id: `${stepId}-${index}`,
          originalId: stepId,
          name: stepName
        };
      });

      const uniqueSteps = formattedSteps
        .filter((step, index, self) => 
          index === self.findIndex((s) => s.name === step.name) && 
          step.name.toLowerCase() !== 'no action' && 
          step.name.toLowerCase() !== 'noaction'
        );

      setSteps(uniqueSteps);
    } else {
      const mockSteps = [
        { id: 'extraction', originalId: 'extraction', name: 'Extraction' },
        { id: 'classification', originalId: 'classification', name: 'Classification' },
        { id: 'validation', originalId: 'validation', name: 'Validation' },
      ];
      setSteps(mockSteps);
    }
    setSelectedStep('');
    setOutputData([]);
  }, [selectedWorkflow, stepsData]);

  // Handle step selection
  const handleStepChange = (event) => {
    const stepId = event.target.value;
    setSelectedStep(stepId);
    setOutputData([]);
  };

  // Check if the selected step is valid
  const isStepValid = selectedStep && selectedStep !== '' && selectedStep !== 'no-action';

  // Process grid configuration from API response
  const processGridConfigSync = (configData) => {
    if (!configData) return {
      columns: [],
      errors: [],
      filterModel: {
        items: [],
        quickFilterValues: [],
      },
      columnVisibilityModel: {},
      headerQuery: '',
      filteredRows: []
    };

    const newConfig = {
      columns: [],
      errors: [],
      filterModel: {
        items: [],
        quickFilterValues: [],
      },
      columnVisibilityModel: {},
      headerQuery: '',
      filteredRows: []
    };

    // Process columns - SAME AS CREDITCARD
    if (configData.grid?.columns) {
      newConfig.columns = configData.grid.columns
        .filter(col => col.visible !== false)
        .map(col => {
          // Width from config may be a string like "200px" – normalize to a number
          let normalizedWidth = 150;
          if (col.width != null) {
            if (typeof col.width === 'number') {
              normalizedWidth = col.width > 0 ? col.width : 150;
            } else if (typeof col.width === 'string') {
              const parsed = parseInt(col.width, 10);
              normalizedWidth = Number.isFinite(parsed) && parsed > 0 ? parsed : 150;
            }
          }
          const dataType = col.DataType ? col.DataType.toLowerCase() : 'string';
          const isNumber = ['number', 'decimal', 'float', 'double', 'integer', 'int', 'bigint'].includes(dataType);
          const isDate = ['date', 'datetime', 'timestamp', 'time'].includes(dataType);
          const isAmount = col.name.toLowerCase().includes('amount') || 
                          col.name.toLowerCase().includes('total') || 
                          col.title?.toLowerCase().includes('amount');
          const isPrice = col.name.toLowerCase().includes('price') || col.title?.toLowerCase().includes('price');
          const isQuantity = col.name.toLowerCase().includes('qty') || col.name.toLowerCase().includes('quantity') ||
            col.title?.toLowerCase().includes('qty') || col.title?.toLowerCase().includes('quantity');

          return {
            field: col.name,
            headerName: col.title || col.name,
            type: isNumber ? 'number' : isDate ? 'date' : 'string',
            width: normalizedWidth,
            flex: 1,
            minWidth: normalizedWidth,
            ...(isDate ? { filterOperators: getDateFilterOperators() } : {}),
            valueGetter: (params) => {
              const value = params.value;
              if (value === null || value === undefined) return isDate ? null : '';
              if (isDate) {
                let d;
                if (value instanceof Date) {
                  d = value;
                } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                  const [y, mm, dd] = value.split('-').map(Number);
                  d = new Date(y, mm - 1, dd);
                } else {
                  d = new Date(value);
                }
                return isNaN(d.getTime()) ? null : d;
              }
              if (typeof value === 'object') return JSON.stringify(value);
              return value;
            },
            renderCell: (params) => {
              const value = params.value;
              if (value === null || value === undefined || value === '') return '-';
              
              // Handle date columns - EXACTLY LIKE CREDITCARD
              if (isDate) {
                try {
                  return formatDate(value);
                } catch (e) {
                  console.error(`Error formatting date for ${col.name}:`, e);
                  try {
                    return format(new Date(value), 'MMM dd, yyyy');
                  } catch {
                    return String(value);
                  }
                }
              }

              // Handle number columns - use amount format for all numbers
              if (isNumber) {
                try {
                  const numValue = parseFloat(value);
                  return isNaN(numValue) ? value : formatAmount(numValue);
                } catch (e) {
                  console.error(`Error formatting value for ${col.name}:`, e);
                  return value;
                }
              }

              // Handle objects
              if (typeof value === 'object') {
                return (
                  <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                    {JSON.stringify(value)}
                  </span>
                );
              }
              
              return String(value);
            },
            // Alignment
            headerAlign: isNumber ? 'right' : 'left',
            align: isNumber ? 'right' : 'left'
          };
        });
    }

    // Process errors
    if (configData.grid?.errors) {
      newConfig.errors = configData.grid.errors.map(error => {
        if (typeof error === 'string') {
          return error;
        }
        if (typeof error === 'object' && error !== null) {
          return error.validation_message || JSON.stringify(error);
        }
        return String(error);
      });
    }

    newConfig.viewGrid = configData.view_grid === true;
    newConfig.printEnabled = configData.print === true || configData.print === 'excel';
    newConfig.printType = configData.print === 'excel' ? 'excel' : 'pdf';

    return newConfig;
  };

  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
  const [gridConfig, setGridConfig] = useState({
    columns: [],
    errors: [],
    viewGrid: true,
    printEnabled: true,
    printType: 'excel',
    filterModel: {
      items: [],
      quickFilterValues: [],
    },
    columnVisibilityModel: {},
    headerQuery: '',
    filteredRows: []
  });

  // Serializable columns for saving - only visible columns
  const serializeColumns = (columns, visibilityModel = {}) => {
    const visibleColumns = columns.filter(col => visibilityModel[col.field] !== false);
    return JSON.stringify({
      columns: visibleColumns.map(col => ({
        field: col.field,
        headerName: col.headerName,
        width: col.width,
        type: col.type
      }))
    });
  };

  // Fetch saved views
  const fetchSavedViews = useCallback(async (categoryOverride) => {
    const category = categoryOverride || gridConfig.viewName;
    if (!category || !originalConfig) {
      setSavedViews([]);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const tenantId = getTenantId();
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/view-configurations?tenantId=${tenantId}&category=${encodeURIComponent(category)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch saved views');
      }

      const data = await response.json();
      const filtered = data.filter(view => view.category === category);
      setSavedViews(filtered);
    } catch (error) {
      console.error('Error fetching saved views:', error);
      setSavedViews([]);
    }
  }, [gridConfig.viewName, originalConfig]);

  useEffect(() => {
    fetchSavedViews();
  }, [fetchSavedViews]);

  // Handle loading a saved view
  const handleLoadView = (view) => {
    try {
      const configJson = JSON.parse(view.columnConfigJson);
      const savedColumnsConfig = configJson.columns;

      let savedFields = [];
      let savedProps = {};

      if (Array.isArray(savedColumnsConfig) && savedColumnsConfig.length > 0) {
        if (typeof savedColumnsConfig[0] === 'string') {
          savedFields = savedColumnsConfig;
        } else {
          savedFields = savedColumnsConfig.map(c => c.field);
          savedColumnsConfig.forEach(c => {
            savedProps[c.field] = {
              headerName: c.headerName,
              width: c.width,
              type: c.type
            };
          });
        }
      }

      const visibilityModel = {};
      const updatedColumns = originalConfig.columns.map(col => {
        const field = col.field;
        const isVisible = savedFields.includes(field);
        visibilityModel[field] = isVisible;

        if (isVisible) {
          const props = savedProps[field] || {};
          return {
            ...col,
            headerName: props.headerName || col.headerName,
            width: props.width || col.width,
            type: props.type || col.type,
            flex: (props.width || col.width) ? 0 : 1,
            minWidth: props.width || col.width || col.minWidth
          };
        } else {
          return col;
        }
      });

      setGridConfig(prev => ({
        ...prev,
        columns: updatedColumns,
        columnVisibilityModel: visibilityModel
      }));
      // Track the applied saved view separately so gridConfig can be replaced safely
      setAppliedSavedViewName(view.viewName);

      // Refresh saved views for this new category immediately
      fetchSavedViews(originalConfig?.viewName || gridConfig.viewName);

      showSuccess(`Loaded view: ${view.viewName}`);
    } catch (error) {
      console.error('Error loading view:', error);
      showError('Failed to load saved view');
    }
  };

  // Handle filter model change
  const handleFilterModelChange = (newFilterModel) => {
    setGridConfig(prev => {
      const updatedConfig = {
        ...prev,
        filterModel: {
          ...prev.filterModel,
          ...newFilterModel
        }
      };

      if (outputData.length > 0) {
        const filtered = applyFilters(outputData, updatedConfig.filterModel);
        updatedConfig.filteredRows = filtered;
      }

      return updatedConfig;
    });
  };

  // Apply filters to data
  const applyFilters = (data, filterModel) => {
    const { items, quickFilterValues } = filterModel;

    let filteredData = [...data];
    if (quickFilterValues && quickFilterValues.length > 0) {
      const searchValue = quickFilterValues[0].toLowerCase();
      filteredData = filteredData.filter(row =>
        Object.values(row).some(value =>
          String(value || '').toLowerCase().includes(searchValue)
        )
      );
    }

    if (items && items.length > 0) {
      filteredData = filteredData.filter(row => {
        return items.every(filterItem => {
          const { field, operator, value } = filterItem;
          const cellValue = row[field];

          // Empty filter -> treat as no-op
          if (value == null || value === '') return true;
          // If cell is missing, it cannot match (except empty checks)
          if (cellValue == null && operator !== 'isEmpty' && operator !== 'isNotEmpty') return false;

          // Check if this column is a date column based on gridConfig
          const colDef = (gridConfig.columns || []).find(c => c.field === field);
          const isDateColumn = colDef && (colDef.type === 'date' || colDef.type === 'datetime' || colDef.type === 'timestamp' || colDef.type === 'time');

          if (isDateColumn) {
            // Parse filter value as ISO YYYY-MM-DD if possible, otherwise try Date
            let filterDate = null;
            if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
              const [y, m, d] = value.split('-').map(Number);
              filterDate = new Date(y, m - 1, d);
            } else {
              const parsed = new Date(value);
              if (!isNaN(parsed.getTime())) filterDate = parsed;
            }
            if (!filterDate) {
              // Could not parse filter value - skip this filter
              return true;
            }

            // Normalize times to local midnight for comparison
            const filterTime = new Date(filterDate.getFullYear(), filterDate.getMonth(), filterDate.getDate()).getTime();

            // Get cell time
            let cellDate = null;
            if (cellValue instanceof Date) {
              cellDate = cellValue;
            } else if (typeof cellValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(cellValue)) {
              const [y, m, d] = cellValue.split('-').map(Number);
              cellDate = new Date(y, m - 1, d);
            } else {
              const parsed = new Date(cellValue);
              if (!isNaN(parsed.getTime())) cellDate = parsed;
            }
            if (!cellDate) return false;
            const cellTime = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate()).getTime();

            switch (operator) {
              case 'is':
                return cellTime === filterTime;
              case 'not':
              case 'isNot':
                return cellTime !== filterTime;
              case 'after':
                return cellTime > filterTime;
              case 'onOrAfter':
                return cellTime >= filterTime;
              case 'before':
                return cellTime < filterTime;
              case 'onOrBefore':
                return cellTime <= filterTime;
              case 'isEmpty':
                return cellValue == null;
              case 'isNotEmpty':
                return cellValue != null;
              default:
                return true;
            }
          }

          // Numeric column handling
          const isNumberColumn = colDef && (colDef.type === 'number' || colDef.type === 'int' || colDef.type === 'integer' || colDef.type === 'float' || colDef.type === 'decimal' || colDef.type === 'double' || colDef.type === 'bigint');
          if (isNumberColumn) {
            const cellNum = typeof cellValue === 'number' ? cellValue : parseFloat(String(cellValue).replace(/,/g, ''));
            const filterNum = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
            if (isNaN(filterNum) || isNaN(cellNum)) {
              // If parsing failed, treat as non-match (except empty checks handled earlier)
              return false;
            }

            switch (operator) {
              case '>':
              case 'greaterThan':
              case 'gt':
                return cellNum > filterNum;
              case '>=':
              case 'onOrAfter':
              case 'gte':
                return cellNum >= filterNum;
              case '<':
              case 'lessThan':
              case 'lt':
                return cellNum < filterNum;
              case '<=':
              case 'onOrBefore':
              case 'lte':
                return cellNum <= filterNum;
              case 'equals':
              case 'is':
              case '==':
              case '=':
                return cellNum === filterNum;
              case 'not':
              case '!=':
              case '<>':
                return cellNum !== filterNum;
              case 'isEmpty':
                return cellValue == null || String(cellValue) === '';
              case 'isNotEmpty':
                return !(cellValue == null || String(cellValue) === '');
              default:
                // Fallback to string compare if unknown operator
                const cellValueStrFallback = String(cellValue).toLowerCase();
                const filterValueFallback = String(value).toLowerCase();
                return cellValueStrFallback === filterValueFallback;
            }
          }

          // Non-date, non-number columns - text matching
          const cellValueStr = String(cellValue).toLowerCase();
          const filterValue = String(value).toLowerCase();

          switch (operator) {
            case 'contains':
              return cellValueStr.includes(filterValue);
            case 'equals':
            case 'is':
              return cellValueStr === filterValue;
            case 'startsWith':
              return cellValueStr.startsWith(filterValue);
            case 'endsWith':
              return cellValueStr.endsWith(filterValue);
            case 'isEmpty':
              return cellValue == null || String(cellValue) === '';
            case 'isNotEmpty':
              return !(cellValue == null || String(cellValue) === '');
            default:
              return true;
          }
        });
      });
    }

    return filteredData;
  };

  // Handle header query change
  const handleHeaderQueryChange = (event) => {
    const value = event.target.value;
    setGridConfig(prev => ({
      ...prev,
      headerQuery: value,
      filterModel: {
        items: [],
        quickFilterValues: value ? [value] : []
      }
    }));
  };

  // Fetch output data
  const handleFetchOutput = async () => {
    if (!canView) {
      showError('You do not have permission to view workflow details');
      return;
    }
    if (!selectedWorkflow || !selectedStep) return;
    // When loading new data, clear any applied saved view marker so menu state stays consistent
    setAppliedSavedViewName(null);

    try {
      if (!isConfigLoaded) {
        showError('Configuration not loaded yet. Please wait...');
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = localStorage.getItem('tenant_id');
      const tenantIntegrationId = localStorage.getItem('tenant_integration');

      const selectedStepData = steps.find(step => step.id === selectedStep);
      const stepName = selectedStepData?.name || '';

      console.log('Fetching data for:', { tenantId, stepName, selectedStep });

      const tenantWorkflowId = selectedWorkflow?.workflowDefinitionSnowflakeId;

      if (!tenantWorkflowId) {
        throw new Error('Workflow definition ID not found');
      }

      // First API call: Get workflow step details
      const stepResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/workflow-steps/search/case?` +
        `tenantId=${encodeURIComponent(tenantId)}&` +
        `stepNamePattern=${encodeURIComponent(stepName)}&` +
        `tenantWorkflowId=${encodeURIComponent(tenantWorkflowId)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!stepResponse.ok) {
        throw new Error('Failed to fetch workflow steps');
      }

      const stepData = await stepResponse.json();
      console.log('Step data:', stepData);

      const stepDetails = Array.isArray(stepData) ? stepData[0] : stepData;
      let viewName = '';

      try {
        const additionalInstructions = JSON.parse(stepDetails.additionalInstructions || '{}');
        viewName = additionalInstructions?.grid?.view_name || '';
      } catch (e) {
        console.error('Error parsing additionalInstructions:', e);
      }

      if (!viewName) {
        throw new Error('View name not found in step details');
      }

      // Get the config
      const configResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/config/default?category=${encodeURIComponent(viewName)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!configResponse.ok) {
        throw new Error('Failed to fetch view configuration');
      }

      const configData = await configResponse.json();
      console.log('Config data:', JSON.stringify(configData, null, 2));

      const effectiveViewName = configData?.category || viewName;

      // Fetch the view data
      const viewDataResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}fastapi/v1/workflow/view-data?` +
        `view_name=${encodeURIComponent(effectiveViewName)}&` +
        `tenant_id=${encodeURIComponent(tenantIntegrationId)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!viewDataResponse.ok) {
        throw new Error('Failed to fetch view data');
      }

      const viewData = await viewDataResponse.json();
      console.log('View data:', JSON.stringify(viewData, null, 2));

      if (!configData || !viewData) {
        throw new Error('Empty response from server');
      }

      // Process grid configuration
      const processedConfig = processGridConfigSync(configData);
      console.log('Processed config columns:', processedConfig.columns);

      // Process data
      const responseData = viewData.data || [];
      let formattedData = Array.isArray(responseData) ? responseData : [responseData].filter(Boolean);

      // Filter out rows where SORT_ORDER = 1
      formattedData = formattedData.filter(row => row.SORT_ORDER !== 1 && row.SORT_ORDER !== '1');

      console.log('Formatted data:', formattedData);

      const safeStringify = (value) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
      };

      const dataWithIds = formattedData.map((item, index) => {
        const row = {};

        Object.entries(item).forEach(([key, value]) => {
          if (value !== null && typeof value === 'object') {
            row[key] = safeStringify(value);
          } else if (value !== undefined) {
            row[key] = value;
          }
        });

        row.id = item.id || item._id || `row-${index}`;

        if (processedConfig.columns.length > 0) {
          processedConfig.columns.forEach(col => {
            const value = row[col.field];
            if (value === null || value === undefined) return;

            if (col.type === 'number') {
              const num = parseFloat(value);
              if (!isNaN(num)) row[col.field] = num;
            } else if (col.type === 'date' && value) {
              let date;
              if (value instanceof Date) {
                date = value;
              } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
                const [y, mm, dd] = value.split('-').map(Number);
                date = new Date(y, mm - 1, dd);
              } else {
                date = new Date(value);
              }
              if (!isNaN(date.getTime())) row[col.field] = date;
            }
          });
        }

        return row;
      });

      console.log('Processed data with IDs:', dataWithIds);

      const fullProcessedConfig = {
        ...processedConfig,
        filteredRows: [...dataWithIds],
        viewName: effectiveViewName
      };

      setOriginalConfig(fullProcessedConfig);
      setGridConfig(fullProcessedConfig);
      setOutputData(dataWithIds);

      // Log the output data view
      try {
        const { AuditLoggers } = await import('../../../utils/auditLogger');
        const selectedStepData = steps.find(step => step.id === selectedStep);
        const stepName = selectedStepData?.name || selectedStep;
        
        await AuditLoggers.outputDataViewed(
          selectedWorkflow.id,
          selectedWorkflow.name,
          selectedStep,
          stepName,
          effectiveViewName
        );
      } catch (error) {
        console.error('Error logging output data view:', error);
        // Continue even if logging fails
      }

    } catch (error) {
      console.error('Error in handleFetchOutput:', error);
      showError(error.message || 'Failed to load workflow data');
    } finally {
      setLoading(false);
    }
  };

  // Reset to default view
  const handleResetToDefault = () => {
    if (!originalConfig) {
      showError('No default configuration available');
      return;
    }

    setGridConfig({
      ...originalConfig,
      columnVisibilityModel: {},
      filterModel: {
        items: [],
        quickFilterValues: [],
      },
      headerQuery: '',
      filteredRows: outputData
    });
    setAppliedSavedViewName(null);
  };

  // Open save view dialog
  const handleSaveView = () => {
    // If a saved view is currently applied, ask whether to Update or Save As New
    if (appliedSavedViewName || gridConfig?.appliedSavedViewName) {
      setSaveModeDialogOpen(true);
    } else {
      setCustomViewName('');
      setSaveDialogOpen(true);
    }
  };

  // Save custom view
  const handleSaveCustomView = async () => {
    if (!customViewName.trim()) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = getTenantId();
      const category = gridConfig.viewName;

      const columnConfigJson = serializeColumns(gridConfig.columns, gridConfig.columnVisibilityModel);
      const defaultColumnConfigJson = serializeColumns(originalConfig?.columns || [], {});

      const payload = {
        viewName: customViewName.trim(),
        columnConfigJson,
        defaultColumnConfigJson,
        category,
        tenantId
      };

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}conductor/view-configurations`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save view');
      }

      // Close dialog first, then show success message and refresh views
      setSaveDialogOpen(false);
      setCustomViewName('');
      showSuccess('Custom view saved successfully');
      // mark this new view as applied and refresh saved views
      setAppliedSavedViewName(payload.viewName);
      await fetchSavedViews(category);
    } catch (error) {
      console.error('Error saving view:', error);
      showError('Failed to save custom view');
    } finally {
      setLoading(false);
    }
  };

  // Update an existing saved view (PUT)
  const handleUpdateSavedView = async () => {
    try {
      setSaveModeLoading(true);
      const token = localStorage.getItem('token');
      const tenantId = getTenantId();
      const appliedName = appliedSavedViewName || gridConfig?.appliedSavedViewName;
      const existing = savedViews.find(v => v.viewName === appliedName);
      if (!existing) throw new Error('Saved view not found to update');

      const viewId = existing.viewId || existing.id;
      const columnConfigJson = serializeColumns(gridConfig.columns, gridConfig.columnVisibilityModel);
      const defaultColumnConfigJson = serializeColumns(originalConfig?.columns || [], {});
      const payload = {
        viewName: existing.viewName,
        columnConfigJson,
        defaultColumnConfigJson,
        category: gridConfig.viewName,
        tenantId
      };

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}conductor/view-configurations/${viewId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to update view');
      }

      setSaveModeDialogOpen(false);
      showSuccess('View updated successfully');
      // refresh saved views for current category
      await fetchSavedViews(gridConfig.viewName);
      // ensure appliedSavedViewName stays set
      setAppliedSavedViewName(existing.viewName);
    } catch (error) {
      console.error('Error updating saved view:', error);
      showError(error.message || 'Failed to update view');
    } finally {
      setSaveModeLoading(false);
    }
  };

  // Menu handlers
  const handleMenuClick = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMenuPosition({
      top: rect.bottom,
      left: rect.left,
    });
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
  };

  const menuOpen = Boolean(menuPosition);
  
  // Custom overlay components
  const NoRowsOverlay = () => (
    <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
      <Typography>No rows to display</Typography>
    </div>
  );

  const ErrorOverlay = ({ error }) => (
    <div style={{ color: 'red', padding: '10px' }}>
      Error loading data: {error?.message || 'Unknown error'}
    </div>
  );

  // Helper function to export data to CSV
  const exportToCSV = (data, fileName) => {
    // Determine which columns should be exported: respect current grid configuration and visibility model
    const allCols = getColumns();
    const visibility = gridConfig?.columnVisibilityModel || {};
    const columnsToExport = (gridConfig?.columns && gridConfig.columns.length > 0)
      ? gridConfig.columns.filter(col => visibility[col.field] !== false)
      : allCols.filter(col => visibility[col.field] !== false);

    const headers = columnsToExport.map(col => col.headerName).join(',');
    const currentDateFormat = getFormatConfig()?.DateFormat || 'DD/MM/YYYY';
    const rows = data.map(row =>
      columnsToExport.map(col => {
        const value = row[col.field];
        // Handle values with commas, quotes, newlines
        if (value === null || value === undefined) return '';

        let stringValue;
        if (value instanceof Date && !isNaN(value.getTime())) {
          // Format Date objects using app format so CSV matches grid display
          stringValue = formatDate(value, currentDateFormat);
        } else if (typeof value === 'object') {
          stringValue = JSON.stringify(value);
        } else {
          stringValue = String(value);
        }

        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    ).join('\n');

    const csv = `${headers}\n${rows}`;
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' }); // Added BOM for Excel compatibility
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const exportToExcel = (data, fileName) => {
    const allCols = getColumns();
    const visibility = gridConfig?.columnVisibilityModel || {};
    const columnsToExport = (gridConfig?.columns && gridConfig.columns.length > 0)
      ? gridConfig.columns.filter(col => visibility[col.field] !== false)
      : allCols.filter(col => visibility[col.field] !== false);

    const currentDateFormat = getFormatConfig()?.DateFormat || 'DD/MM/YYYY';
    const headerRow = columnsToExport.map(col => col.headerName || col.field);
    const rows = data.map(row =>
      columnsToExport.map(col => {
        const value = row[col.field];
        if (value === null || value === undefined) return '';
        if (value instanceof Date && !isNaN(value.getTime())) {
          return formatDate(value, currentDateFormat);
        }
        if (typeof value === 'object') return JSON.stringify(value);
        return value;
      })
    );

    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...rows]);
    const workbook = XLSX.utils.book_new();
    const selectedStepData = steps.find(step => step.id === selectedStep);
    const sheetName = String(selectedStepData?.name || 'Output').slice(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, fileName);
  };

  // Handle custom export functionality
  const handleCustomExport = async (format = 'csv') => {
    try {
      const exportType = format.toUpperCase();
      const timestamp = new Date().toISOString().slice(0, 10);
      const fileName = `${selectedWorkflow?.name || 'export'}-${selectedStep || 'data'}-${timestamp}.${format}`;
      
      // Get data to export:
      // - Prefer gridConfig.filteredRows when available (keeps any pre-computed filtered set)
      // - Otherwise, if a filter model or quick filter is active, apply the same filters to outputData
      // - Fallback to the full outputData
      let dataToExport = outputData;
      if (gridConfig.filteredRows && gridConfig.filteredRows.length > 0) {
        dataToExport = gridConfig.filteredRows;
      } else if (
        (gridConfig.filterModel && Array.isArray(gridConfig.filterModel.items) && gridConfig.filterModel.items.length > 0) ||
        (gridConfig.filterModel && Array.isArray(gridConfig.filterModel.quickFilterValues) && gridConfig.filterModel.quickFilterValues.length > 0)
      ) {
        try {
          dataToExport = applyFilters(outputData, gridConfig.filterModel);
        } catch (e) {
          console.warn('Failed to compute filtered rows for export, exporting full data instead', e);
          dataToExport = outputData;
        }
      }
      
      // Log the export action
      const selectedStepData = steps.find(step => step.id === selectedStep);
      const stepName = selectedStepData?.name || selectedStep;
      
      // Use the new outputDataExported audit logger
      await AuditLoggers.outputDataExported(
        selectedWorkflow?.id,
        selectedWorkflow?.name,
        selectedStep,
        stepName,
        gridConfig?.viewName || 'unknown',
        exportType,
        dataToExport.length
      );
      
      console.log(`${exportType} export audit logged successfully`);
      
      // Perform the actual export
      if (format === 'csv') {
        exportToCSV(dataToExport, fileName);
        showSuccess(`Exported ${dataToExport.length} rows to CSV`);
      } else if (format === 'xlsx') {
        exportToExcel(dataToExport, fileName);
        showSuccess(`Exported ${dataToExport.length} rows to Excel`);
      } else {
        console.log('Excel export not implemented yet');
      }
    } catch (error) {
      console.error('Error during export:', error);
      showError('Failed to export data');
    }
  };

  // Get columns for DataGrid
  const getColumns = () => {
    console.log('Getting columns, gridConfig:', JSON.stringify(gridConfig, null, 2));
    if (gridConfig.columns && gridConfig.columns.length > 0) {
      console.log('Using configured columns:', gridConfig.columns);
      return gridConfig.columns;
    }

    if (outputData.length > 0) {
      const firstRow = outputData[0];
      return Object.keys(firstRow).map(key => ({
        field: key,
        headerName: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1').trim(),
        flex: key === 'id' ? 0 : 1,
        width: key === 'id' ? 100 : undefined,
      }));
    }

    return [
      { field: 'id', headerName: 'ID', width: 100 },
      { field: 'field1', headerName: 'Field 1', flex: 1 },
      { field: 'field2', headerName: 'Field 2', flex: 1 },
    ];
  };

  if (!isConfigLoaded) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1">Loading application configuration...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...harmonatePageSx, minHeight: '100vh' }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={harmonateBreadcrumbSx}>
          <Link
            underline="hover"
            color="inherit"
            onClick={onBackToWorkflows}
            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <HomeIcon sx={{ fontSize: '1rem' }} />
            {pageTitle}
          </Link>
          <Typography sx={{ color: ht.white, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AssessmentIcon sx={{ fontSize: '1rem' }} />
            Output Viewer
          </Typography>
        </Breadcrumbs>
      </Box>

      <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 3, mb: 3 }}>
        <Grid container spacing={3} sx={harmonateFormFieldsSx}>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="workflow-select-label">Select Workflow</InputLabel>
              <Select
                labelId="workflow-select-label"
                value={selectedWorkflowId}
                onChange={handleWorkflowChange}
                label="Select Workflow"
                fullWidth
                disabled={isLoadingWorkflows}
              >
                <MenuItem value="">
                  <em>Select a workflow</em>
                </MenuItem>
                {workflows.map((workflow) => (
                  <MenuItem key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <FormControl fullWidth variant="outlined" size="small" disabled={!selectedWorkflow}>
              <InputLabel id="step-select-label">Select Step</InputLabel>
              <Select
                labelId="step-select-label"
                value={selectedStep}
                onChange={handleStepChange}
                label="Select Step"
                fullWidth
                disabled={!selectedWorkflow || isLoadingSteps}
              >
                <MenuItem value="">
                  <em>Select a step</em>
                </MenuItem>
                {steps.map((step) => (
                  <MenuItem key={step.id} value={step.id}>
                    {step.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={!canView ? 'You do not have permission to view workflow details' : ''}>
              <span style={{ width: '100%' }}>
                <Button
                  variant="contained"
                  onClick={handleFetchOutput}
                  disabled={!canView || !selectedWorkflow || !isStepValid || loading}
                  fullWidth
                  sx={{ ...harmonateProceedButtonSx, height: '40px' }}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? 'Loading...' : 'Load Data'}
                </Button>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ ...harmonateCardSx, bgcolor: ht.white, p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress sx={{ color: ht.green }} />
          </Box>
        ) : outputData.length > 0 ? (
          <Box sx={{ width: '100%', height: 'calc(100vh - 300px)' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="textSecondary">
                Rows: {gridConfig.filteredRows?.length > 0 ? `${gridConfig.filteredRows.length} of ` : ''}{outputData.length} | Columns: {getColumns().length}
              </Typography>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search in all columns..."
                value={gridConfig.headerQuery}
                onChange={handleHeaderQueryChange}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ height: 'calc(100% - 30px)' }}>
              <DataGridPro
                columnResizeMode="client"
                pagination
                rowsPerPageOptions={[10, 25, 50]}
                pageSize={10}
                autoPageSize={false}
                resizable
                rows={outputData}
                columns={getColumns()}
                columnVisibilityModel={gridConfig.columnVisibilityModel}
                disableSelectionOnClick
                disableColumnMenu={false}
                filterModel={gridConfig.filterModel}
                onFilterModelChange={handleFilterModelChange}
                components={{
                  Toolbar: () => (
                    <div style={{ padding: '8px' }}>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <GridToolbarColumnsButton />
                        <GridToolbarFilterButton />
                        <GridToolbarDensitySelector />
                        {canExport && (
                          <Button
                            size="small"
                            onClick={() => handleCustomExport('csv')}
                            sx={{ 
                              textTransform: 'none',
                              color: ht.text,
                              '&:hover': {
                                backgroundColor: 'rgba(46, 166, 160, 0.08)'
                              }
                            }}
                          >
                            Export CSV
                          </Button>
                        )}
                        {canExport && (
                          <Button
                            size="small"
                            onClick={() => handleCustomExport('xlsx')}
                            sx={{
                              textTransform: 'none',
                              color: ht.text,
                              '&:hover': {
                                backgroundColor: 'rgba(46, 166, 160, 0.08)'
                              }
                            }}
                          >
                            Export Excel
                          </Button>
                        )}
                        <Button
                          size="small"
                          onClick={() => {
                            if (gridConfig.viewName) {
                              setIsCustomizeModalOpen(true);
                            } else {
                              showError?.('Please load data first');
                            }
                          }}
                          sx={{ 
                            textTransform: 'none',
                            color: 'primary.main',
                            '&:hover': {
                              backgroundColor: 'rgba(102, 187, 106, 0.04)'
                            }
                          }}
                          startIcon={<TuneIcon fontSize="small" />}
                        >
                          Customize
                        </Button>
                        <Box sx={{ position: 'relative' }}>
                          <Button
                            size="small"
                            onClick={handleMenuClick}
                            sx={{ 
                              textTransform: 'none',
                              color: ht.text,
                              '&:hover': {
                                backgroundColor: 'rgba(46, 166, 160, 0.08)'
                              }
                            }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </Button>
                          <Menu
                            anchorEl={null}
                            open={menuOpen}
                            onClose={handleMenuClose}
                            anchorReference="anchorPosition"
                            anchorPosition={menuPosition}
                            PaperProps={{
                              sx: {
                                mt: 0.5
                              }
                            }}
                          >
                            <MenuItem onClick={() => { handleResetToDefault(); handleMenuClose(); }}>
                              {(!appliedSavedViewName && gridConfig.viewName && originalConfig && gridConfig.viewName === originalConfig.viewName) ? <CheckIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> : <span style={{ width: 20, display: 'inline-block' }} />}
                              Default View
                            </MenuItem>
                            <Divider />
                            {savedViews.map((view) => {
                              const isActiveView = appliedSavedViewName && view.viewName && appliedSavedViewName === view.viewName;
                              return (
                                <MenuItem
                                  key={view.viewId}
                                  onClick={() => { handleLoadView(view); handleMenuClose(); }}
                                >
                                  {isActiveView ? <CheckIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} /> : <span style={{ width: 20, display: 'inline-block' }} />}
                                  {view.viewName}
                                </MenuItem>
                              );
                            })}
                            <Divider />
                            <MenuItem onClick={() => { handleSaveView(); handleMenuClose(); }}>
                              Save View
                            </MenuItem>
                          </Menu>
                        </Box>
                      </div>
                    </div>
                  ),
                  NoRowsOverlay: NoRowsOverlay,
                  ErrorOverlay: ErrorOverlay
                }}
                componentsProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                sx={{
                  ...harmonateDataGridSx,
                  '& .MuiDataGrid-cell': {
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                  },
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, color: 'text.secondary' }}>
            <Typography>Select a workflow and step, then click 'Load Data' to view output</Typography>
          </Box>
        )}
      </Paper>
      <ColumnCustomizationModal
        open={isCustomizeModalOpen}
        onClose={() => setIsCustomizeModalOpen(false)}
        viewName={gridConfig.viewName}
        initialColumns={gridConfig.columns}
        initialVisibilityModel={gridConfig.columnVisibilityModel}
        onSave={(updatedColumns) => {
          console.log('Updated columns from modal:', updatedColumns);
          
          // Create a map of column names to their updated properties
          const columnUpdates = {};
          updatedColumns.forEach(col => {
            columnUpdates[col.name] = {
              visible: col.visible,
              width: col.width,
              title: col.title
            };
          });

          console.log('Column updates map:', columnUpdates);

          // Update the grid config with the new column properties
          setGridConfig(prev => {
            console.log('Previous columns:', prev.columns);
            console.log('Previous visibility model:', prev.columnVisibilityModel);
            
            // Build column visibility model - explicitly set ALL columns
            const visibilityModel = {};
            const updatedCols = prev.columns.map(col => {
              const updates = columnUpdates[col.field];
              
              console.log(`Processing column ${col.field}:`, updates);
              
              // Explicitly set visibility for every column
              if (updates) {
                visibilityModel[col.field] = updates.visible !== false;
                console.log(`Setting ${col.field} visibility to: ${visibilityModel[col.field]}`);
              } else {
                // If no update info, keep existing visibility or default to true
                visibilityModel[col.field] = prev.columnVisibilityModel?.[col.field] !== false;
              }
              
              // Ensure width is respected by setting flex to 0 if custom width, else 1
              const customWidth = updates?.width !== undefined ? updates.width : col.width;
              const newCol = {
                ...col,
                width: customWidth,
                minWidth: customWidth,
                flex: customWidth ? 0 : 1,
                headerName: updates?.title || col.headerName || col.field
              };
              
              return newCol;
            });

            console.log('Updated columns:', updatedCols);
            console.log('New visibility model:', visibilityModel);

            const newConfig = {
              ...prev,
              columns: updatedCols,
              columnVisibilityModel: visibilityModel
            };

            console.log('Final grid config:', newConfig);
            return newConfig;
          });
        }}
      />
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Save Custom View</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx }}>
          <TextField
            autoFocus
            margin="dense"
            label="View Name"
            placeholder="Enter view name"
            fullWidth
            variant="outlined"
            value={customViewName}
            onChange={(e) => setCustomViewName(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveDialogOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button 
            onClick={handleSaveCustomView} 
            variant="contained" 
            disabled={!customViewName.trim() || loading}
            sx={harmonateProceedButtonSx}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {/* Save mode dialog - when a saved view is already applied */}
      <Dialog open={saveModeDialogOpen} onClose={() => setSaveModeDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Save View</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A saved view is currently applied. Would you like to update the existing view or save as a new view?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setSaveModeDialogOpen(false); setSaveDialogOpen(true); }} variant="outlined" sx={harmonateCancelButtonSx} disabled={saveModeLoading}>
            Save As New
          </Button>
          <Button onClick={handleUpdateSavedView} variant="contained" disabled={saveModeLoading} sx={harmonateProceedButtonSx}>
            {saveModeLoading ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutputViewer;